import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DictationButton } from '@/components/DictationButton';
import { toast } from 'sonner';
import {
  HelpCircle, X, Send, Sparkles, ChevronRight, ChevronLeft,
  LayoutDashboard, Users, FileText, Landmark, Globe, Settings2,
  Rocket, MapPin, Zap, ArrowRight,
} from 'lucide-react';

/**
 * TutorialAndHelp — Tanda 17 Fase 5 (Enriched in Fase B).
 *
 * Rich 8-step guided tour with:
 *   - Inline SVG illustrations per section (theme-aware, lightweight)
 *   - Structured content: "Qué hace", "Cómo usarlo", "Dónde está"
 *   - Highlights list of the top features
 *   - "Ir a la sección" quick-nav shortcut
 *   - Progress dots + segmented progress bar
 *   - Floating help button with AI chat and re-run tour
 */

// ------------------------------------------------------------
// Illustrations — inline SVGs so we don't need CDN/asset hosting.
// Each is 320x180, uses currentColor + navy accent for consistency.
// ------------------------------------------------------------

const Illustration = ({ children }) => (
  <div
    className="relative h-40 w-full rounded-lg bg-gradient-to-br from-slate-50 to-white border border-slate-200 flex items-center justify-center overflow-hidden"
    aria-hidden="true"
  >
    <div
      className="absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgb(15 23 42) 1px, transparent 0)',
        backgroundSize: '16px 16px',
      }}
    />
    <svg viewBox="0 0 320 160" className="w-full h-full relative z-10">
      {children}
    </svg>
  </div>
);

/**
 * TourMedia — renders per-step media based on media_type:
 *   - "svg"        → fallback SVG component (default)
 *   - "gif"|"image" → <img src={media_url}/>
 *   - "video_url"  → iframe (YouTube/Loom)
 *   - "mp4"        → <video src={media_url or base64 data URL}/>
 */
function TourMedia({ media_type, media_url, media_mp4_base64, fallback: Fallback }) {
  const wrapCls = "relative h-40 w-full rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden";
  if (!media_type || media_type === 'svg') {
    return Fallback ? <Fallback /> : <div className={wrapCls} />;
  }
  if (media_type === 'gif' || media_type === 'image') {
    return (
      <div className={wrapCls}>
        <img src={media_url} alt="" className="w-full h-full object-cover" data-testid="tour-media-image" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      </div>
    );
  }
  if (media_type === 'video_url') {
    // Normalize YouTube / Loom URLs to embed form
    let src = media_url || '';
    const ytMatch = src.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (ytMatch) src = `https://www.youtube.com/embed/${ytMatch[1]}`;
    const loomMatch = src.match(/loom\.com\/share\/([\w-]+)/);
    if (loomMatch) src = `https://www.loom.com/embed/${loomMatch[1]}`;
    return (
      <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-900">
        <iframe src={src} title="Tour video" allow="accelerometer; encrypted-media; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-testid="tour-media-iframe" />
      </div>
    );
  }
  if (media_type === 'mp4') {
    const src = media_url || (media_mp4_base64 ? `data:video/mp4;base64,${media_mp4_base64}` : '');
    return (
      <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-900">
        <video src={src} controls className="absolute inset-0 w-full h-full object-contain" data-testid="tour-media-video" />
      </div>
    );
  }
  return Fallback ? <Fallback /> : <div className={wrapCls} />;
}

const IllustrationWelcome = () => (
  <Illustration>
    <defs>
      <linearGradient id="welcome-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0f172a" />
        <stop offset="100%" stopColor="#1e40af" />
      </linearGradient>
    </defs>
    {/* Confetti dots */}
    <circle cx="40" cy="30" r="3" fill="#0f172a" opacity="0.3" />
    <circle cx="280" cy="40" r="3" fill="#1e40af" opacity="0.3" />
    <circle cx="60" cy="120" r="2.5" fill="#0f172a" opacity="0.25" />
    <circle cx="270" cy="130" r="3" fill="#1e40af" opacity="0.35" />
    <circle cx="140" cy="20" r="2" fill="#0f172a" opacity="0.2" />
    {/* Rocket */}
    <g transform="translate(160 80)">
      <path d="M0 -35 C 10 -35, 18 -15, 18 5 L 18 20 L -18 20 L -18 5 C -18 -15, -10 -35, 0 -35 Z" fill="url(#welcome-grad)" />
      <circle cx="0" cy="-5" r="6" fill="#f8fafc" />
      <path d="M-18 12 L -30 30 L -18 22 Z" fill="#1e40af" opacity="0.85" />
      <path d="M18 12 L 30 30 L 18 22 Z" fill="#1e40af" opacity="0.85" />
      <path d="M-6 22 L 0 40 L 6 22 Z" fill="#f97316" />
      <path d="M-3 30 L 0 44 L 3 30 Z" fill="#facc15" />
    </g>
  </Illustration>
);

const IllustrationDashboard = () => (
  <Illustration>
    {/* Sidebar */}
    <rect x="10" y="15" width="50" height="130" rx="6" fill="#0f172a" opacity="0.06" />
    <rect x="18" y="30" width="34" height="4" rx="2" fill="#0f172a" opacity="0.4" />
    <rect x="18" y="42" width="26" height="4" rx="2" fill="#0f172a" opacity="0.25" />
    <rect x="18" y="54" width="30" height="4" rx="2" fill="#0f172a" opacity="0.25" />
    <rect x="18" y="66" width="22" height="4" rx="2" fill="#0f172a" opacity="0.25" />
    {/* KPI cards */}
    <rect x="70" y="15" width="70" height="40" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
    <rect x="78" y="22" width="30" height="3" rx="1.5" fill="#94a3b8" />
    <text x="78" y="42" fontSize="12" fontWeight="700" fill="#0f172a">$12.4k</text>

    <rect x="145" y="15" width="70" height="40" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
    <rect x="153" y="22" width="34" height="3" rx="1.5" fill="#94a3b8" />
    <text x="153" y="42" fontSize="12" fontWeight="700" fill="#0f172a">28</text>

    <rect x="220" y="15" width="90" height="40" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
    <rect x="228" y="22" width="42" height="3" rx="1.5" fill="#94a3b8" />
    <text x="228" y="42" fontSize="12" fontWeight="700" fill="#0f172a">96%</text>

    {/* Chart */}
    <rect x="70" y="65" width="240" height="80" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
    <polyline
      points="80,130 110,110 140,120 170,90 200,100 230,75 260,85 295,60"
      fill="none" stroke="#1e40af" strokeWidth="2.5"
    />
    <polyline
      points="80,130 110,110 140,120 170,90 200,100 230,75 260,85 295,60 295,140 80,140"
      fill="#1e40af" opacity="0.08"
    />
    {[
      [80, 130], [110, 110], [140, 120], [170, 90], [200, 100], [230, 75], [260, 85], [295, 60],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="2.5" fill="#1e40af" />
    ))}
  </Illustration>
);

const IllustrationClients = () => (
  <Illustration>
    {[0, 1, 2].map((i) => {
      const y = 25 + i * 42;
      const initials = ['MG', 'JR', 'AL'][i];
      const names = ['María González', 'Juan Rodríguez', 'Ana López'];
      const colors = ['#1e40af', '#0f172a', '#0891b2'];
      return (
        <g key={i}>
          <rect x="20" y={y} width="280" height="34" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
          <circle cx="42" cy={y + 17} r="12" fill={colors[i]} />
          <text x="42" y={y + 21} fontSize="10" fontWeight="700" fill="#fff" textAnchor="middle">{initials}</text>
          <text x="62" y={y + 15} fontSize="10" fontWeight="600" fill="#0f172a">{names[i]}</text>
          <text x="62" y={y + 27} fontSize="8" fill="#64748b">3 casos activos</text>
          <rect x="240" y={y + 10} width="50" height="14" rx="7" fill="#dcfce7" />
          <text x="265" y={y + 20} fontSize="8" fontWeight="600" fill="#166534" textAnchor="middle">Al día</text>
        </g>
      );
    })}
  </Illustration>
);

const IllustrationInvoices = () => (
  <Illustration>
    {/* Invoice paper */}
    <g transform="translate(80 15)">
      <rect x="0" y="0" width="160" height="130" rx="6" fill="#ffffff" stroke="#cbd5e1" />
      <rect x="12" y="12" width="70" height="6" rx="2" fill="#0f172a" />
      <rect x="12" y="24" width="40" height="4" rx="2" fill="#94a3b8" />
      <rect x="12" y="46" width="136" height="1" fill="#e2e8f0" />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="12" y={54 + i * 14} width="70" height="4" rx="2" fill="#334155" />
          <rect x="120" y={54 + i * 14} width="28" height="4" rx="2" fill="#334155" />
        </g>
      ))}
      <rect x="12" y="102" width="136" height="1" fill="#e2e8f0" />
      <text x="12" y="118" fontSize="8" fontWeight="600" fill="#0f172a">Total</text>
      <text x="148" y="118" fontSize="10" fontWeight="800" fill="#1e40af" textAnchor="end">$3,250.00</text>
    </g>
    {/* Discount badge */}
    <g transform="translate(220 55)">
      <rect x="0" y="0" width="80" height="24" rx="12" fill="#1e40af" />
      <text x="40" y="16" fontSize="10" fontWeight="700" fill="#fff" textAnchor="middle">-15% pronto</text>
    </g>
    {/* Payment card */}
    <g transform="translate(220 90)">
      <rect x="0" y="0" width="80" height="46" rx="6" fill="#0f172a" />
      <rect x="8" y="10" width="26" height="8" rx="2" fill="#facc15" />
      <text x="8" y="34" fontSize="7" fill="#94a3b8">•••• 4242</text>
    </g>
  </Illustration>
);

const IllustrationTrust = () => (
  <Illustration>
    {/* Vault */}
    <g transform="translate(50 15)">
      <rect x="0" y="0" width="120" height="130" rx="8" fill="#0f172a" />
      <rect x="10" y="10" width="100" height="110" rx="4" fill="#1e293b" />
      <circle cx="60" cy="65" r="30" fill="#0f172a" stroke="#475569" strokeWidth="2" />
      <circle cx="60" cy="65" r="20" fill="#1e293b" stroke="#64748b" strokeWidth="1.5" />
      <circle cx="60" cy="65" r="4" fill="#facc15" />
      <line x1="60" y1="65" x2="60" y2="48" stroke="#facc15" strokeWidth="2" strokeLinecap="round" />
      <line x1="60" y1="65" x2="73" y2="65" stroke="#facc15" strokeWidth="2" strokeLinecap="round" />
    </g>
    {/* Balance card */}
    <g transform="translate(190 25)">
      <rect x="0" y="0" width="110" height="50" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
      <text x="10" y="20" fontSize="8" fill="#64748b">Balance IOLTA</text>
      <text x="10" y="38" fontSize="14" fontWeight="800" fill="#0f172a">$8,500.00</text>
    </g>
    {/* Progress ring */}
    <g transform="translate(190 90)">
      <rect x="0" y="0" width="110" height="55" rx="6" fill="#f8fafc" stroke="#e2e8f0" />
      <text x="10" y="18" fontSize="8" fill="#64748b">Contratado $15k</text>
      <rect x="10" y="26" width="90" height="6" rx="3" fill="#e2e8f0" />
      <rect x="10" y="26" width="55" height="6" rx="3" fill="#1e40af" />
      <text x="10" y="46" fontSize="8" fontWeight="600" fill="#0f172a">57% aplicado</text>
    </g>
  </Illustration>
);

const IllustrationPortal = () => (
  <Illustration>
    {/* Device mockup */}
    <g transform="translate(30 15)">
      <rect x="0" y="0" width="120" height="130" rx="10" fill="#0f172a" />
      <rect x="6" y="6" width="108" height="118" rx="6" fill="#ffffff" />
      <rect x="14" y="16" width="60" height="6" rx="2" fill="#0f172a" />
      <rect x="14" y="30" width="40" height="4" rx="2" fill="#94a3b8" />
      <rect x="14" y="46" width="92" height="20" rx="4" fill="#f1f5f9" />
      <text x="20" y="59" fontSize="8" fontWeight="600" fill="#0f172a">Factura #INV-042</text>
      <rect x="14" y="72" width="92" height="20" rx="4" fill="#f1f5f9" />
      <text x="20" y="85" fontSize="8" fontWeight="600" fill="#0f172a">Factura #INV-041</text>
      <rect x="14" y="100" width="92" height="14" rx="7" fill="#1e40af" />
      <text x="60" y="110" fontSize="8" fontWeight="700" fill="#fff" textAnchor="middle">Pagar ahora</text>
    </g>
    {/* Secure link chip */}
    <g transform="translate(170 45)">
      <rect x="0" y="0" width="130" height="30" rx="15" fill="#dcfce7" stroke="#22c55e" strokeWidth="1" />
      <circle cx="18" cy="15" r="6" fill="#22c55e" />
      <path d="M15 15 L 17 17 L 21 13" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <text x="30" y="19" fontSize="9" fontWeight="600" fill="#166534">Enlace seguro TLS</text>
    </g>
    {/* Payment options */}
    <g transform="translate(170 85)">
      <rect x="0" y="0" width="40" height="26" rx="4" fill="#635bff" />
      <text x="20" y="18" fontSize="10" fontWeight="700" fill="#fff" textAnchor="middle">S</text>
      <rect x="46" y="0" width="40" height="26" rx="4" fill="#ffb3c7" />
      <text x="66" y="18" fontSize="7" fontWeight="700" fill="#0f172a" textAnchor="middle">Klarna</text>
      <rect x="92" y="0" width="40" height="26" rx="4" fill="#facc15" />
      <text x="112" y="18" fontSize="8" fontWeight="700" fill="#0f172a" textAnchor="middle">ATH</text>
    </g>
  </Illustration>
);

const IllustrationSettings = () => (
  <Illustration>
    {/* Gear */}
    <g transform="translate(70 80)">
      <circle cx="0" cy="0" r="30" fill="none" stroke="#0f172a" strokeWidth="4" />
      <circle cx="0" cy="0" r="12" fill="#0f172a" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = Math.cos(rad) * 30;
        const y1 = Math.sin(rad) * 30;
        const x2 = Math.cos(rad) * 42;
        const y2 = Math.sin(rad) * 42;
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0f172a" strokeWidth="6" strokeLinecap="round" />;
      })}
    </g>
    {/* Toggles */}
    <g transform="translate(160 25)">
      {['Marca', 'Impuestos', 'SMTP', 'Stripe'].map((label, i) => (
        <g key={label} transform={`translate(0 ${i * 27})`}>
          <text x="0" y="16" fontSize="10" fontWeight="500" fill="#0f172a">{label}</text>
          <rect x="90" y="6" width="28" height="14" rx="7" fill={i < 3 ? '#1e40af' : '#cbd5e1'} />
          <circle cx={i < 3 ? 111 : 97} cy="13" r="5" fill="#fff" />
        </g>
      ))}
    </g>
  </Illustration>
);

const IllustrationReady = () => (
  <Illustration>
    <defs>
      <linearGradient id="ready-grad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#1e40af" />
        <stop offset="100%" stopColor="#0f172a" />
      </linearGradient>
    </defs>
    {/* Ribbon check */}
    <g transform="translate(160 80)">
      <circle cx="0" cy="0" r="42" fill="url(#ready-grad)" />
      <circle cx="0" cy="0" r="42" fill="none" stroke="#1e40af" strokeWidth="6" opacity="0.15" />
      <path d="M-18 0 L -4 14 L 20 -12" stroke="#fff" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    {/* Stars */}
    {[[50, 30, 4], [270, 40, 3], [40, 130, 3], [280, 125, 5], [230, 25, 3]].map(([x, y, r], i) => (
      <path
        key={i}
        d={`M${x} ${y - r} L ${x + r * 0.3} ${y - r * 0.3} L ${x + r} ${y} L ${x + r * 0.3} ${y + r * 0.3} L ${x} ${y + r} L ${x - r * 0.3} ${y + r * 0.3} L ${x - r} ${y} L ${x - r * 0.3} ${y - r * 0.3} Z`}
        fill="#facc15"
        opacity="0.85"
      />
    ))}
  </Illustration>
);

// ------------------------------------------------------------
// Tour Steps — richer structure per step.
// ------------------------------------------------------------

const TOUR_STEPS = [
  {
    step_key: 'welcome',
    icon: Rocket,
    title: 'Bienvenido a EZ DockIT',
    subtitle: 'Tour rápido — 8 pasos, menos de 2 minutos',
    illustration: IllustrationWelcome,
    body: 'Le mostraremos las secciones principales y para qué sirve cada una. Puede saltarla en cualquier momento y volver a abrirla desde el botón flotante ? en la esquina inferior derecha.',
    highlights: [
      '8 secciones esenciales',
      'Explicaciones claras',
      'Puede volver cuando quiera',
    ],
  },
  {
    step_key: 'dashboard',
    icon: LayoutDashboard,
    title: 'Panel principal',
    subtitle: 'Su vista general del bufete',
    illustration: IllustrationDashboard,
    what: 'Métricas clave en tiempo real: ingresos del mes, facturas pendientes, tareas del día y actividad reciente.',
    how: 'Su punto de partida cada mañana. Los cards son clickeables — llevan al detalle correspondiente.',
    where: 'Menú lateral > Panel',
    navTo: '/',
    highlights: [
      'Ingresos por mes y año',
      'Facturas pendientes y vencidas',
      'Acceso rápido a acciones frecuentes',
    ],
  },
  {
    step_key: 'clients',
    icon: Users,
    title: 'Clientes',
    subtitle: 'El corazón de su bufete',
    illustration: IllustrationClients,
    what: 'Cree y administre a los clientes de su firma. Cada cliente puede tener múltiples casos y cuentas de fideicomiso independientes.',
    how: 'Toque "Nuevo cliente" o busque uno existente. Al abrir un cliente verá su historial completo, insights financieros y estado de fideicomiso.',
    where: 'Menú lateral > Clientes',
    navTo: '/clients',
    highlights: [
      'Casos y fideicomisos por cliente',
      'Insights financieros automáticos',
      'Historial completo de facturación',
    ],
  },
  {
    step_key: 'invoices',
    icon: FileText,
    title: 'Facturas',
    subtitle: 'Genere y cobre en segundos',
    illustration: IllustrationInvoices,
    what: 'Cree facturas con descuentos por pronto pago, cupones o descuentos manuales. Envíelas al portal del cliente o por correo con PDF adjunto.',
    how: 'Toque "Nueva factura" → seleccione cliente → agregue conceptos → aplique descuentos si desea. La factura queda disponible para pago con tarjeta, Klarna, ATH Móvil o fideicomiso.',
    where: 'Menú lateral > Facturas',
    navTo: '/receipts',
    highlights: [
      'Stripe, Klarna, ATH Móvil, IOLTA',
      'Descuentos manuales y automáticos',
      'PDF profesional listo para enviar',
    ],
  },
  {
    step_key: 'trust',
    icon: Landmark,
    title: 'Fideicomiso (IOLTA)',
    subtitle: 'Cuentas separadas por caso',
    illustration: IllustrationTrust,
    what: 'Cuentas de fideicomiso multi-caso con balance inicial contratado. Notificación automática cuando el saldo baje del 50% del monto contratado.',
    how: 'Cada cliente puede tener varias cuentas (una por caso). Aplique fondos del fideicomiso a facturas específicas desde la vista del cliente.',
    where: 'Menú lateral > Fideicomiso',
    navTo: '/trust',
    highlights: [
      'Multi-caso por cliente',
      'Alerta al bajar del 50%',
      'Aplicación transparente a facturas',
    ],
  },
  {
    step_key: 'portal',
    icon: Globe,
    title: 'Portal del cliente',
    subtitle: 'Sus clientes pagan solos',
    illustration: IllustrationPortal,
    what: 'Los clientes acceden con un enlace seguro que usted comparte. Pueden pagar, ver su historial y solicitar aplicar fondos del fideicomiso desde allí.',
    how: 'En la vista del cliente, toque "Enviar enlace al portal". El enlace es único por cliente y se puede regenerar por seguridad. También puede ir al portal directamente desde /portal.',
    where: 'Vista de cliente > Enlace al portal',
    highlights: [
      'Pago con Stripe / Klarna / ATH',
      'Historial descargable',
      'Solicitud de aplicar fideicomiso',
    ],
  },
  {
    step_key: 'settings',
    icon: Settings2,
    title: 'Configuración',
    subtitle: 'Personalice su firma',
    illustration: IllustrationSettings,
    what: 'Ajuste marca (logo, colores), impuestos, SMTP para correos, Stripe por perfil emisor y todas las políticas de descuentos y pagos.',
    how: 'Vaya a Configuración y navegue por las pestañas. La sección "Perfiles emisores" le permite emitir facturas con diferentes marcas si su firma opera bajo varios nombres.',
    where: 'Menú lateral > Configuración',
    navTo: '/settings',
    highlights: [
      'Múltiples perfiles emisores',
      'SMTP y Stripe por perfil',
      'Legal, retención y auditoría',
    ],
  },
  {
    step_key: 'ready',
    icon: Sparkles,
    title: '¡Listo para empezar!',
    subtitle: 'Y recuerde: el asistente IA está siempre disponible',
    illustration: IllustrationReady,
    body: 'Puede volver a esta guía cuando quiera desde el botón ? flotante. Y si tiene dudas, pregúntele al asistente de IA — responde en español y le guía paso a paso por cualquier flujo.',
    highlights: [
      'Botón ? flotante siempre visible',
      'Asistente IA en español',
      'Documentación integrada',
    ],
  },
];

// ------------------------------------------------------------
// AI Helper Chat panel (kept largely as-is, cosmetics only)
// ------------------------------------------------------------

export function AIHelperChat({ open, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        '¡Hola! Soy su asistente de EZ DockIT. Puedo explicarle cualquier sección, ayudarle con flujos (crear factura, aplicar fideicomiso, agregar cliente), o responder dudas sobre configuración. ¿Qué desea saber? También puede presionar el botón 🎤 para dictar una factura.',
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `helper_${Math.random().toString(36).slice(2)}`);
  const endRef = useRef();
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  if (!open) return null;

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setInput('');
    setBusy(true);
    try {
      const { data } = await api.post('/ai-helper/chat', {
        session_id: sessionId,
        message: msg,
      });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply || 'Sin respuesta' }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: 'Error al contactar al asistente. Verifique conexión.' },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    '¿Cómo creo una factura?',
    '¿Cómo aplico fideicomiso?',
    '¿Cómo comparto el portal?',
  ];

  /**
   * Handle dictation output from within the chat: stash payload in
   * sessionStorage so QuickReceipt picks it up on mount, then navigate.
   */
  const handleDictationParsed = (payload) => {
    try {
      sessionStorage.setItem('ezdockit.dictation.pending', JSON.stringify({
        payload,
        ts: Date.now(),
      }));
      toast.success('Abriendo Nueva Factura con lo dictado…');
      onClose?.();
      setTimeout(() => navigate('/nuevo-recibo'), 60);
    } catch (e) {
      toast.error('No se pudo abrir Nueva Factura');
    }
  };

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-white border-l border-slate-200 shadow-2xl flex flex-col"
      data-testid="ai-helper-panel"
    >
      <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-slate-800 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-amber-400" />
          </div>
          <div>
            <div className="font-semibold text-sm leading-tight">Asistente EZ DockIT</div>
            <div className="text-[10px] text-slate-400 leading-tight">Impulsado por IA</div>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="text-white hover:bg-slate-800 h-7 w-7 p-0"
          data-testid="ai-helper-close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] ${m.role === 'user' ? 'ml-auto' : ''}`}>
            <div
              className={`rounded-2xl px-3 py-2 text-sm ${
                m.role === 'user' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-1 text-xs text-slate-500 italic">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-pulse" />
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-pulse [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-pulse [animation-delay:300ms]" />
            <span className="ml-1">Escribiendo…</span>
          </div>
        )}
        {messages.length === 1 && !busy && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => setInput(s)}
                className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
                data-testid={`ai-helper-suggestion-${s.slice(0, 6)}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-200 flex gap-2 items-center">
        <DictationButton
          onParsed={handleDictationParsed}
          compact={true}
          testId="ai-helper-dictate-btn"
          label="Dictar factura"
        />
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !busy && send()}
          placeholder="Pregunte o dicte con 🎤…"
          className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          disabled={busy}
          data-testid="ai-helper-input"
        />
        <Button
          onClick={send}
          disabled={busy || !input.trim()}
          size="sm"
          data-testid="ai-helper-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Enriched Tutorial Tour (Fase B)
// ------------------------------------------------------------

export function TutorialTour({ open, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const [dontShow, setDontShow] = useState(false);
  const [overrides, setOverrides] = useState({}); // step_key -> override
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    api.get('/tour/steps').then(({ data }) => {
      const map = {};
      // globals first, firm overrides win
      (data.globals || []).forEach(o => { if (o.step_key) map[o.step_key] = o; });
      (data.firm_overrides || []).forEach(o => { if (o.step_key) map[o.step_key] = { ...(map[o.step_key] || {}), ...o }; });
      setOverrides(map);
    }).catch(() => setOverrides({}));
  }, [open]);

  if (!open) return null;

  const rawStep = TOUR_STEPS[step];
  const ov = overrides[rawStep.step_key] || {};
  const s = { ...rawStep, ...ov };  // override wins for fields that exist
  const isLast = step === TOUR_STEPS.length - 1;
  const isFirst = step === 0;
  const Illust = rawStep.illustration; // default SVG
  const Icon = rawStep.icon;

  const finish = async () => {
    if (dontShow || isLast) {
      try {
        await api.post('/auth/tutorial-complete', { completed: true });
      } catch {
        // ignore — local flag already set by parent
      }
      onComplete?.();
    }
    onClose();
  };

  const goToSection = () => {
    if (s.navTo) {
      onClose();
      navigate(s.navTo);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      data-testid="tutorial-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
    >
      <Card className="max-w-xl w-full shadow-2xl border-slate-200 max-h-[90vh] overflow-hidden flex flex-col">
        <CardContent className="p-0 flex flex-col overflow-hidden">
          {/* Header — icon + step counter + segmented progress */}
          <div className="px-6 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-slate-900 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                  Paso {step + 1} de {TOUR_STEPS.length}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={finish}
                className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900"
                data-testid="tutorial-close-x"
                aria-label="Cerrar tour"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {/* Segmented progress bar */}
            <div className="flex items-center gap-1">
              {TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    i <= step ? 'bg-slate-900' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content scroll area */}
          <div className="overflow-y-auto flex-1 px-6 py-5">
            {/* Media/Illustration */}
            <div className="mb-4">
              <TourMedia media_type={s.media_type} media_url={s.media_url} media_mp4_base64={s.media_mp4_base64} fallback={Illust} />
            </div>

            {/* Title + subtitle */}
            <h2
              id="tutorial-title"
              className="font-display text-2xl font-semibold text-slate-900 leading-tight mb-1"
            >
              {s.title}
            </h2>
            {s.subtitle && (
              <p className="text-sm text-slate-500 mb-4">{s.subtitle}</p>
            )}

            {/* Structured body */}
            {s.body ? (
              <p className="text-sm text-slate-700 leading-relaxed mb-4">{s.body}</p>
            ) : (
              <div className="space-y-3 mb-4">
                {s.what && (
                  <div className="flex gap-3">
                    <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-slate-900 text-white flex items-center justify-center">
                      <Sparkles className="h-3 w-3" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
                        Qué hace
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{s.what}</p>
                    </div>
                  </div>
                )}
                {s.how && (
                  <div className="flex gap-3">
                    <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center">
                      <Zap className="h-3 w-3" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
                        Cómo usarlo
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{s.how}</p>
                    </div>
                  </div>
                )}
                {s.where && (
                  <div className="flex gap-3">
                    <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-amber-500 text-white flex items-center justify-center">
                      <MapPin className="h-3 w-3" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
                        Dónde está
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{s.where}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Highlights */}
            {s.highlights && s.highlights.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {s.highlights.map((h) => (
                  <Badge
                    key={h}
                    variant="secondary"
                    className="bg-slate-100 text-slate-700 hover:bg-slate-100 font-normal text-[11px]"
                  >
                    {h}
                  </Badge>
                ))}
              </div>
            )}

            {/* Quick nav */}
            {s.navTo && (
              <div className="mt-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={goToSection}
                  className="text-xs"
                  data-testid="tutorial-goto-section"
                >
                  Ir a esta sección
                  <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3 flex-wrap">
            <label
              className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none"
              data-testid="tutorial-dontshow-label"
            >
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => setDontShow(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300"
                data-testid="tutorial-dontshow"
              />
              No volver a mostrar
            </label>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep((s) => s - 1)}
                  data-testid="tutorial-prev"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                  Anterior
                </Button>
              )}
              {isFirst && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={finish}
                  data-testid="tutorial-skip"
                >
                  Saltar
                </Button>
              )}
              {!isLast ? (
                <Button
                  size="sm"
                  onClick={() => setStep((s) => s + 1)}
                  data-testid="tutorial-next"
                >
                  Siguiente
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={finish}
                  data-testid="tutorial-finish"
                >
                  ¡Empezar!
                  <Rocket className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------
// Floating help button — always visible.
// ------------------------------------------------------------

export default function TutorialAndHelp() {
  const { user } = useAuth() || {};
  const [tourOpen, setTourOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Auto-open tour on first login (once). E2E tests can bypass by setting
  // localStorage.firmpay_tutorial_global_skip='1' via add_init_script — never
  // via URL params (would allow anyone with the link to skip onboarding for
  // all users). See Iteration 5D feedback.
  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem('firmpay_tutorial_global_skip') === '1') return;
    } catch (_) { /* non-browser env */ }
    const key = `firmpay_tutorial_seen_${user.id}`;
    const seenLocal = localStorage.getItem(key);
    const seenServer = user.tutorial_completed;
    if (!seenLocal && !seenServer) {
      setTourOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const closeTour = () => {
    if (user) localStorage.setItem(`firmpay_tutorial_seen_${user.id}`, '1');
    setTourOpen(false);
  };

  if (!user) return null;

  return (
    <>
      <TutorialTour open={tourOpen} onClose={closeTour} onComplete={closeTour} />
      <AIHelperChat open={chatOpen} onClose={() => setChatOpen(false)} />
      <div className="fixed bottom-6 right-6 z-40">
        {menuOpen && (
          <div
            className="mb-2 bg-white border border-slate-200 rounded-xl shadow-lg p-1 flex flex-col gap-0.5 min-w-[220px]"
            data-testid="help-menu"
          >
            <button
              onClick={() => {
                setChatOpen(true);
                setMenuOpen(false);
              }}
              className="text-sm text-left px-3 py-2 rounded-md hover:bg-slate-100 flex items-center gap-2"
              data-testid="help-menu-chat"
            >
              <Sparkles className="h-4 w-4 text-amber-500" />
              <div className="flex-1">
                <div className="font-medium text-slate-900">Asistente IA</div>
                <div className="text-[11px] text-slate-500">Pregúntele en español</div>
              </div>
            </button>
            <button
              onClick={() => {
                setTourOpen(true);
                setMenuOpen(false);
              }}
              className="text-sm text-left px-3 py-2 rounded-md hover:bg-slate-100 flex items-center gap-2"
              data-testid="help-menu-tour"
            >
              <HelpCircle className="h-4 w-4 text-blue-600" />
              <div className="flex-1">
                <div className="font-medium text-slate-900">Ver guía de inicio</div>
                <div className="text-[11px] text-slate-500">8 pasos rápidos</div>
              </div>
            </button>
          </div>
        )}
        <Button
          size="lg"
          className="h-12 w-12 rounded-full shadow-lg p-0"
          onClick={() => setMenuOpen((m) => !m)}
          data-testid="help-floating-btn"
          title="Ayuda"
          aria-label="Abrir menú de ayuda"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <HelpCircle className="h-5 w-5" />}
        </Button>
      </div>
    </>
  );
}
